import React, { useMemo, useState } from 'react';
import Select from '@atlaskit/select';
import { FieldProps } from '@atlaskit/form';
import { formatAddressLabel } from '@css-xp/base';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';

export type SoldToAddressesType = CommerceAddress[] | undefined;

export interface CommerceAddressSelect {
  label: string;
  value: CommerceAddress;
  isCompanyName?: boolean;
}

const getAllAddressesOption = (companyName: string) => {
  return {
    label: 'All sold-to addresses',
    value: { id: companyName },
    isCompanyName: true,
  };
};

export const SelectCustomerAddress = ({
  onSelect,
  soldToAddresses,
  defaultSoldToAddress,
  customClassName,
  isLoading,
  isAllSoldToAddressDisabled = false,
  skipCompanyName = true,
  fieldProps,
  handleAddNewAddressClick,
  showAddNewAddressOption,
}: {
  onSelect: (value: any, isCompanyName?: boolean) => void;
  soldToAddresses: SoldToAddressesType;
  defaultSoldToAddress?: CommerceAddress;
  customClassName?: string;
  isLoading?: boolean;
  isAllSoldToAddressDisabled?: boolean;
  skipCompanyName?: boolean;
  fieldProps?: FieldProps<string, HTMLInputElement>;
  handleAddNewAddressClick?: () => void;
  showAddNewAddressOption?: boolean;
}) => {
  const companyName = soldToAddresses?.[0]?.name ?? '';
  const selectOptions = useMemo(() => {
    if (soldToAddresses) {
      const addressOptions: CommerceAddressSelect[] = soldToAddresses?.map((node) => {
        return {
          label: node && formatAddressLabel(node, skipCompanyName),
          value: node,
          isCompanyName: false,
        };
      });
      {
        !isAllSoldToAddressDisabled && addressOptions.push(getAllAddressesOption(companyName));
      }
      if (showAddNewAddressOption) {
        addressOptions.unshift({
          label: '+ Add new address',
          value: { id: 'add-new-address' },
          isCompanyName: false,
        });
      }
      return addressOptions;
    }
  }, [soldToAddresses]);

  const defaultValue = selectOptions?.length
    ? defaultSoldToAddress
      ? selectOptions.find((option) => option.value.id === defaultSoldToAddress.id)
      : selectOptions.find((option) => option.value.id === companyName)
    : null;

  const [value, setValue] = useState(defaultValue);

  const showSelectShipToAddress = () => {
    const hasNoResults = !soldToAddresses || soldToAddresses?.length === 0;
    return !hasNoResults;
  };

  const handleSelect = (option: any) => {
    if (showAddNewAddressOption && option?.value?.id === 'add-new-address' && handleAddNewAddressClick) {
      handleAddNewAddressClick();
    }
    onSelect(option?.value, option.isCompanyName);
    setValue(option);
  };

  return showSelectShipToAddress() ? (
    <Select<CommerceAddressSelect>
      {...fieldProps}
      onChange={handleSelect}
      options={selectOptions}
      placeholder={'Select an existing customer address'}
      testId="select-customer-address"
      classNamePrefix={customClassName}
      className={customClassName}
      isSearchable
      value={value}
      isLoading={isLoading}
    />
  ) : (
    // @ts-ignore
    <Select
      {...fieldProps}
      data-testid={isLoading ? 'select-customer-address-loading' : 'select-customer-address'}
      options={[]}
      className={customClassName}
      placeholder={isLoading ? 'Select an existing customer address' : 'No customer address exist'}
      isLoading={isLoading}
    />
  );
};
